import type { SelectChangeEvent } from '@mui/material/Select'
import { useEffect, useMemo, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'

import PageHeader from '@/components/layouts/PageHeader'
import { DEFAULT_DATA_GRID_FILTER_MODEL, DEFAULT_DATA_GRID_PAGINATION_MODEL } from '@/constants/datagrid'
import { useDataGridSyncUrlManager } from '@/contexts/DataGridSyncUrlManagerContext'
import ResourceGrid from '@/features/resource/components/ResourceGrid'
import ResourcesAppBarContent from '@/features/resource/components/ResourcesAppBarContent'
import { RESOURCE_TYPES } from '@/features/resource/constants'
import type {
  ResourceGlobalFilters,
  ResourceGridActions,
  ResourceGridState,
  ResourceType,
} from '@/features/resource/types'
import { useSitesQuery } from '@/features/site/hooks/useSitesQuery'
import { SiteStatus } from '@/features/site/types/site'
import { getCountryOptions } from '@/utils/countryOptionsFromSites'
import { sortOptionsAlphabetically } from '@/utils/sortOptionsAlphabetically'

const COUNTRY_CODE_FILTER_NAME = 'countryCode'
const SITE_UUID_FILTER_NAME = 'siteUuid'
const RESOURCE_TYPE_FILTER_NAME = 'resourceType'

const gridReducer = (state: ResourceGridState, action: ResourceGridActions): ResourceGridState => {
  switch (action.type) {
    // Reset pagination page to 0 when filters change
    case 'setFilter':
      return {
        ...state,
        filters: action.payload,
      }
    case 'setSort':
      return {
        ...state,
        sort: action.payload,
      }
    case 'setPagination':
      return {
        ...state,
        pagination: action.payload,
      }
    case 'resetPagination':
      return {
        ...state,
        pagination: DEFAULT_DATA_GRID_PAGINATION_MODEL,
      }
    default:
      return state
  }
}

const ResourcesIndex = () => {
  const { t } = useTranslation()
  const syncUrlManager = useDataGridSyncUrlManager()
  const [globalFilters, setGlobalFilters] = useState<ResourceGlobalFilters>({
    countryCode: syncUrlManager.initialState.globalFilters?.[COUNTRY_CODE_FILTER_NAME] ?? '',
    siteUuid: syncUrlManager.initialState.globalFilters?.[SITE_UUID_FILTER_NAME] ?? '',
    resourceType: (syncUrlManager.initialState.globalFilters?.[RESOURCE_TYPE_FILTER_NAME] as ResourceType) ?? undefined,
  })
  const [gridState, gridDispatch] = useReducer(gridReducer, {
    filters: syncUrlManager.initialState.filterModel ?? DEFAULT_DATA_GRID_FILTER_MODEL,
    sort: syncUrlManager.initialState.sortModel ?? [],
    pagination: syncUrlManager.initialState.paginationModel ?? DEFAULT_DATA_GRID_PAGINATION_MODEL,
  })

  const { sites, isLoading } = useSitesQuery()

  const activeSites = useMemo(() => sites?.filter((site) => site.status === SiteStatus.ENABLED) ?? [], [sites])

  const filteredSites = useMemo(() => {
    if (!globalFilters.countryCode) return activeSites
    return activeSites.filter((site) => site.countryCode?.toLowerCase() === globalFilters.countryCode)
  }, [activeSites, globalFilters.countryCode])

  const countryOptions = useMemo(
    () => sortOptionsAlphabetically<string>(getCountryOptions(activeSites, t)),
    [activeSites, t],
  )

  useEffect(() => {
    syncUrlManager.syncGlobalFilterSearchParams(globalFilters)
  }, [globalFilters])

  function handleSiteChange(evt: SelectChangeEvent) {
    const selectedSite = evt.target.value

    setGlobalFilters((prevState) => ({ ...prevState, siteUuid: selectedSite }))
    gridDispatch({ type: 'resetPagination' })
  }

  function handleResourceTypeChange(evt: SelectChangeEvent) {
    const selectedResourceType = evt.target.value as ResourceType

    setGlobalFilters((prevState) => ({ ...prevState, resourceType: selectedResourceType }))
    gridDispatch({ type: 'resetPagination' })
  }

  function handleCountryCodeChange(evt: SelectChangeEvent) {
    const selectedCountryCode = evt.target.value
    setGlobalFilters((prevState) => ({ ...prevState, countryCode: selectedCountryCode, siteUuid: undefined }))

    gridDispatch({ type: 'resetPagination' })
  }

  const selectedSiteName = useMemo(() => {
    if (!globalFilters.siteUuid || !sites) {
      return t('resources.all_customers')
    }
    const site = sites.find((site) => site.uuid === globalFilters.siteUuid)

    return site?.name ?? ''
  }, [globalFilters.siteUuid])

  return (
    <>
      <PageHeader
        isRoot
        appBarContent={
          !isLoading && (
            <ResourcesAppBarContent
              countryCodeOptions={countryOptions}
              globalFilters={globalFilters}
              resourceTypes={RESOURCE_TYPES}
              sites={filteredSites}
              onCountryCodeChange={handleCountryCodeChange}
              onResourceTypeChange={handleResourceTypeChange}
              onSiteChange={handleSiteChange}
            />
          )
        }
        pageTitle={t('component.page_header.resources')}
      />

      <ResourceGrid
        globalFilters={globalFilters}
        gridDispatch={gridDispatch}
        gridState={gridState}
        title={selectedSiteName}
      />
    </>
  )
}

export default ResourcesIndex
